// EmbeddedCheckout.jsx
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { stripePromise } from "../../../constants";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { API_URL, pool } from "../../../constants";

/**
 * Wrapper component — displays a Pay button and opens embedded payment dialog
 * Props:
 *  - user: current user object (id, email, stripe_customer_id)
 *  - cart: { planPriceId, planOption, addOns: [{sku, quantity, price}], totals: {...} }
 *  - onSuccess(): callback to proceed with onboarding
 */

export default function EmbeddedCheckout({ user, cart, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [useSavedCard, setUseSavedCard] = useState(false);
  const [savedMethods, setSavedMethods] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);

  useEffect(() => {
    // fetch saved payment methods (optional)
    const load = async () => {
      setLoadingSaved(true);
      try {
        const res = await axios.get(`${API_URL}/api/payment-methods/`);
        setSavedMethods(res.data || []);
        if ((res.data || []).length > 0) setUseSavedCard(true);
      } catch (err) {
        console.warn("Could not fetch saved cards", err);
      } finally {
        setLoadingSaved(false);
      }
    };
    load();
  }, []);

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpen(true)}
      >
        Pay & Subscribe
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You will subscribe to: <strong>{cart.planName}</strong>
            {cart.addOns?.length ? (
              <>
                {" "}
                and purchase {cart.addOns.length} add-on(s) for €{cart.addOnTotal}.
              </>
            ) : null}
          </Typography>

          {/* Option to use a saved card */}
          {loadingSaved ? (
            <CircularProgress size={20} />
          ) : savedMethods.length ? (
            <FormControlLabel
              control={
                <Switch
                  checked={useSavedCard}
                  onChange={(e) => setUseSavedCard(e.target.checked)}
                />
              }
              label={`Use saved card: ${savedMethods[0]?.brand || ""} ****${savedMethods[0]?.last4 || ""}`}
            />
          ) : null}

          <Elements stripe={stripePromise}>
            {/* Elements mounted only if user wants to enter a new card */}
            {!useSavedCard && (
              <CardPaymentForm
                user={user}
                cart={cart}
                onComplete={() => {
                  setOpen(false);
                  onSuccess && onSuccess();
                }}
              />
            )}

            {/* If using saved card, we still need to call backend to create subscription + charge add-ons */}
            {useSavedCard && (
              <SavedCardFlow
                user={user}
                card={savedMethods[0]}
                cart={cart}
                onComplete={() => {
                  setOpen(false);
                  onSuccess && onSuccess();
                }}
              />
            )}
          </Elements>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* === CardPaymentForm ===
   1) Requests backend to create a SetupIntent (attach card to customer)
   2) Calls stripe.confirmCardSetup with card details
   3) After Setup succeeds, call backend to create subscription + create/confirm add-on PaymentIntent(s)
*/
function CardPaymentForm({ user, cart, onComplete }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handlePay = async () => {
    setError(null);
    setBusy(true);
    try {
      // 1) Request backend to create SetupIntent + provisional subscription order RECORD
      const payload = {
        user_id: user.id,
        plan_option: cart.planPriceId, // server maps to product/price
        addons: cart.addOns || [],
      };
      const { data } = await axios.post(`${API_URL}/api/stripe/create-setup-intent/`, payload);
      const setupClientSecret = data.setup_intent_client_secret;
      // optionally send a server-side 'order_id' to be used later

      // 2) Confirm setup (collect card details)
      const cardElement = elements.getElement(CardElement);
      const confirmRes = await stripe.confirmCardSetup(setupClientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: user.email,
            name: user.full_name || user.email.split("@")[0],
          },
        },
      });

      if (confirmRes.error) {
        setError(confirmRes.error.message);
        setBusy(false);
        return;
      }

      const paymentMethodId = confirmRes.setupIntent.payment_method;

      // 3) Tell backend: setup succeeded -> create subscription (server will attach PM and create sub)
      const subscribeRes = await axios.post(`${API_URL}/api/stripe/complete-subscription/`, {
        user_id: user.id,
        payment_method: paymentMethodId,
        plan_option: cart.planPriceId,
        addons: cart.addOns || [],
      });

      // subscribeRes may include a payment intent client secret for 1) add-ons payment or 2) if invoice requires SCA
      // If backend provides `payment_intent_client_secret`, confirm it here
      if (subscribeRes.data.payment_intent_client_secret) {
        const piSecret = subscribeRes.data.payment_intent_client_secret;
        const conf = await stripe.confirmCardPayment(piSecret);
        if (conf.error) {
          setError(conf.error.message);
          setBusy(false);
          return;
        }
      }

      // Success — advance UI
      onComplete();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || err.message || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <CardElement options={{ hidePostalCode: false }} />
      {error && <Typography color="error">{error}</Typography>}
      <div style={{ marginTop: 16 }}>
        <Button variant="contained" color="primary" onClick={handlePay} disabled={!stripe || busy}>
          {busy ? <CircularProgress size={20} /> : "Pay & Subscribe"}
        </Button>
      </div>
    </div>
  );
}

/* === SavedCardFlow ===
   If user picks saved card, call backend to create subscription and confirm one-time PaymentIntent server-side.
   Backend will either return final success or a PaymentIntent client_secret if SCA is required.
*/
function SavedCardFlow({ user, card, cart, onComplete }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const stripe = useStripe();

  useEffect(() => {
    // nothing; we confirm only when user clicks server flow button (rendered below)
  }, []);

  const handleUseSaved = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/stripe/complete-subscription/`, {
        user_id: user.id,
        use_saved_payment_method: true,
        payment_method_id: card?.stripe_payment_method_id,
        plan_option: cart.planPriceId,
        addons: cart.addOns || [],
      });

      if (res.data.payment_intent_client_secret) {
        // confirm SCA using stripe.confirmCardPayment with the client secret
        const confirmRes = await stripe.confirmCardPayment(res.data.payment_intent_client_secret);
        if (confirmRes.error) {
          setError(confirmRes.error.message);
          setBusy(false);
          return;
        }
      }

      onComplete();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || err.message || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      {error && <Typography color="error">{error}</Typography>}
      <Button onClick={handleUseSaved} variant="contained" disabled={busy || !card}>
        {busy ? <CircularProgress size={20} /> : `Pay with ${card.brand} ****${card.last4}`}
      </Button>
    </div>
  );
}
